/**
 * useTalkToUsLink — TanStack Query composable
 *
 * Fetches the talktous base URL from GET settings/v1/talktous/link.
 * Cached permanently (staleTime: Infinity) — the path never changes.
 *
 * The API always returns a jumper.ai domain regardless of environment.
 * We strip the host and reattach the environment-correct base from
 * shopLivechatBase() so the link is correct on staging and EU too.
 *
 * Usage: append "/wa/<agent_nickname>" to talkToUsBaseUrl for the full agent link.
 */
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import { shopLivechatBase } from '@/helpers/user.helpers'

interface TalkToUsLinkResponse {
  url?: string
  [key: string]: unknown
}

export const talkToUsLinkQueryOptions = {
  queryKey: ['talktous-link'] as const,
  queryFn: async (): Promise<TalkToUsLinkResponse> => {
    const r = await apiInterceptor.get(
      environment('VUE_APP_API_URL') + 'settings/v1/talktous/link'
    )
    return (r as unknown as TalkToUsLinkResponse) ?? {}
  },
  staleTime: Infinity,
}

export function useTalkToUsLink() {
  const { data, isLoading, isError, error } = useQuery(talkToUsLinkQueryOptions)

  // The API returns jumper.ai in the URL regardless of environment.
  // Extract the path (/talktous/<storename>/<hash>) and prepend the
  // environment-correct host so staging gets jumperdev.com, EU gets jumper-emea.com.
  const talkToUsBaseUrl = computed<string | undefined>(() => {
    const apiUrl = data.value?.url
    if (!apiUrl) return undefined
    try {
      const path = new URL(apiUrl).pathname  // e.g. /talktous/extteam/c676807...
      return shopLivechatBase() + path       // e.g. https://shop.jumperdev.com/talktous/extteam/c676807...
    } catch {
      return apiUrl  // fallback: use as-is if URL parsing fails
    }
  })

  return {
    talkToUsBaseUrl,
    isLoading,
    isError,
    error,
  }
}
